'use client'

import React, { useState, useEffect } from 'react'
import { Strategy } from '@/types'
import { X, Save, Loader2 } from 'lucide-react'
import { updateStrategy } from '../lib/api'

interface EditStrategyModalProps {
  strategy: Strategy | null
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const EditStrategyModal: React.FC<EditStrategyModalProps> = ({
  strategy,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    name_zh: '',
    type: 'trend' as any,
    market: 'crypto' as any,
    market_symbol: '',
    timeframe: '1h' as any,
    status: 'draft' as any,
    description: '',
    description_en: '',
    description_zh: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (strategy) {
      setFormData({
        name: strategy.name || '',
        name_en: strategy.name_en || '',
        name_zh: strategy.name_zh || '',
        type: strategy.type || 'trend',
        market: strategy.market || 'crypto',
        market_symbol: strategy.market_symbol || '',
        timeframe: strategy.timeframe || '1h',
        status: strategy.status || 'draft',
        description: strategy.description || '',
        description_en: strategy.description_en || '',
        description_zh: strategy.description_zh || ''
      })
    }
  }, [strategy])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!strategy) return

    try {
      setLoading(true)
      setError(null)

      await updateStrategy({
        strategy_id: strategy.id,
        ...formData
      })

      onSuccess()
      onClose()
    } catch (err) {
      console.error('Error updating strategy:', err)
      setError(err instanceof Error ? err.message : '更新策略失败')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (!isOpen || !strategy) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-blue-50/95 dark:bg-blue-900/95 backdrop-blur-md rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-blue-200/50 dark:border-blue-700/50">
        <div className="flex items-center justify-between p-6 border-b border-blue-200/50 dark:border-blue-700/50">
          <h2 className="text-xl font-semibold text-blue-900 dark:text-white">编辑策略</h2>
          <button
            onClick={onClose}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-4 flex items-center space-x-2">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 策略名称 */}
            <div>
              <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
                策略名称 *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
                required
              />
            </div>

            {/* 英文名称 */}
            <div>
              <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
                英文名称
              </label>
              <input
                type="text"
                value={formData.name_en}
                onChange={(e) => handleChange('name_en', e.target.value)}
                className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {/* 中文名称 */}
            <div>
              <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
                中文名称
              </label>
              <input
                type="text"
                value={formData.name_zh}
                onChange={(e) => handleChange('name_zh', e.target.value)}
                className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {/* 策略类型 */}
            <div>
              <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
                策略类型 *
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
                required
              >
                <option value="trend">趋势跟踪</option>
                <option value="grid">网格交易</option>
                <option value="mean_reversion">均值回归</option>
                <option value="breakout">突破策略</option>
                <option value="scalping">高频交易</option>
                <option value="other">其他</option>
              </select>
            </div>

            {/* 市场类型 */}
            <div>
              <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
                市场类型 *
              </label>
              <select
                value={formData.market}
                onChange={(e) => handleChange('market', e.target.value)}
                className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
                required
              >
                <option value="crypto">加密货币</option>
                <option value="forex">外汇</option>
                <option value="stocks">股票</option>
                <option value="commodities">商品</option>
                <option value="other">其他</option>
              </select>
            </div>

            {/* 交易对 */}
            <div>
              <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
                交易对 *
              </label>
              <input
                type="text"
                value={formData.market_symbol}
                onChange={(e) => handleChange('market_symbol', e.target.value)}
                placeholder="如: BTC/USD, EUR/USD"
                className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
                required
              />
            </div>

            {/* 时间框架 */}
            <div>
              <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
                时间框架 *
              </label>
              <select
                value={formData.timeframe}
                onChange={(e) => handleChange('timeframe', e.target.value)}
                className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
                required
              >
                <option value="1m">1分钟</option>
                <option value="5m">5分钟</option>
                <option value="15m">15分钟</option>
                <option value="30m">30分钟</option>
                <option value="1h">1小时</option>
                <option value="4h">4小时</option>
                <option value="1d">1天</option>
                <option value="1w">1周</option>
                <option value="1M">1月</option>
              </select>
            </div>

            {/* 状态 */}
            <div>
              <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
                状态
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
              >
                <option value="draft">草稿</option>
                <option value="test">验证中</option>
                <option value="live">实盘中</option>
              </select>
            </div>
          </div>

          {/* 策略描述 */}
          <div>
            <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
              策略描述
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
              placeholder="描述策略的基本思路和特点..."
            />
          </div>

          {/* 英文描述 */}
          <div>
            <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
              英文描述
            </label>
            <textarea
              value={formData.description_en}
              onChange={(e) => handleChange('description_en', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
              placeholder="Strategy description in English..."
            />
          </div>

          {/* 中文描述 */}
          <div>
            <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2">
              中文描述
            </label>
            <textarea
              value={formData.description_zh}
              onChange={(e) => handleChange('description_zh', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
              placeholder="策略描述（中文）..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-blue-200/50 dark:border-blue-700/50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-white transition-colors rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50"
              disabled={loading}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center px-6 py-3 whitespace-nowrap"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              保存更改
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditStrategyModal 